import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { currencyService } from '@/services/currencyService';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Badge } from '@vaiu/ui/badge';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Separator } from '@vaiu/ui/separator';
import { 
  ArrowLeft, 
  FileText, 
  Clock, 
  CheckCircle, 
  CreditCard, 
  Loader2,
  AlertCircle,
  Receipt
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { CurrencyDisplay } from '@vaiu/ui';
import { formatDistanceToNow } from 'date-fns';
import { platformApi } from '../services/platformApi';
import { InvoicePaymentDecision } from '@vaiu/redeem-ux';
import { i18n } from '@/utils/i18n';

interface Payment {
  id: string;
  amount: number;
  currency: string;
  date: string;
  payerName?: string;
}

interface InvoiceDetails {
  key: string;
  amount: number;
  currency: string;
  status: string;
  createdDate: string;
  expiryDate: string;
  message?: string;
  creatorName?: string;
  remainingAmount: number;
  paidAmount: number;
  paymentCount: number;
  payments: Payment[];
}

const InvoicePaymentPage: React.FC = () => {
  const { invoiceCode } = useParams<{ invoiceCode: string }>();
  const navigate = useNavigate();
  const { user, isGuest } = useAuth(); // Use isGuest from useAuth
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<InvoiceDetails | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (invoiceCode) {
      loadInvoiceDetails();
    }
  }, [invoiceCode]);

  const loadInvoiceDetails = async (showLoading: boolean = true) => {
    if (!invoiceCode) return;

    if (showLoading) {
      setLoading(true);
      setError('');
    }

    try {
      const data = await platformApi.getInvoiceDetails(invoiceCode);
      
      const totalAmount = parseFloat(String(data.amount));
      const remainderRaw = data.remainder;
      const remainingAmount =
        remainderRaw === undefined || remainderRaw === null || remainderRaw === ''
          ? totalAmount
          : parseFloat(String(remainderRaw));

      const invoiceDetails: InvoiceDetails = {
        key: data.key,
        amount: totalAmount,
        currency: data.currency,
        status: data.status,
        createdDate: data.start,
        expiryDate: data.end,
        message: data.message,
        creatorName: data.creator?.name || 'Unknown',
        remainingAmount: Number.isFinite(remainingAmount) ? remainingAmount : totalAmount,
        paidAmount: totalAmount - (Number.isFinite(remainingAmount) ? remainingAmount : totalAmount),
        paymentCount: data.invoicePayments?.length || 0,
        payments: (data.invoicePayments || []).map((payment: any, index: number) => ({
          id: payment.paymentId || payment.transactionId || `payment-${index}`,
          amount: payment.amount || 0,
          currency: payment.currency || data.currency,
          date: payment.paidAt || new Date().toISOString(),
          payerName: 'Customer'
        }))
      };

      setInvoice(invoiceDetails);
      
    } catch (error: any) {
      console.error('Failed to load invoice:', error);
      setError(i18n.__('invoicePayment.loadingError'));
      toast({
        variant: "destructive",
        title: i18n.__('invoicePayment.toast.error'),
        description: i18n.__('invoicePayment.toast.errorDesc'),
      });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handlePaymentComplete = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    await loadInvoiceDetails(true);
  };

  const handlePaymentError = (error: any) => {
    toast({
      variant: "destructive",
      title: i18n.__('invoicePayment.toast.fail'),
      description: error.message || i18n.__('invoicePayment.toast.failDesc'),
    });
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">{i18n.__('invoicePayment.notFound')}</h2>
          <p className="text-gray-600 mb-4">
            {i18n.__('invoicePayment.notFoundDesc')}
          </p>
          <Button onClick={() => navigate('/')}>
            {i18n.__('invoicePayment.returnHome')}
          </Button>
        </div>
      </div>
    );
  }

  const paidFromHistory = invoice.payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const leftoverFromHistory = Math.round((invoice.amount - paidFromHistory) * 100) / 100;
  const displayRemaining =
    invoice.remainingAmount > 0 ? invoice.remainingAmount : Math.max(0, leftoverFromHistory);
  const isFullyPaid = displayRemaining <= 0;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {i18n.__('invoicePayment.backHome')}
        </Button>
        <div className="flex items-center space-x-3">
          <FileText className="w-8 h-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold">{i18n.__('invoicePayment.title', { key: invoice.key })}</h1>
            <p className="text-gray-600">
              {i18n.__('invoicePayment.created', { timeAgo: formatDistanceToNow(new Date(invoice.createdDate), { addSuffix: true }) })}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Invoice Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{i18n.__('invoicePayment.summary')}</span>
                <Badge variant={isFullyPaid ? "outline" : "destructive"} className={
                  isFullyPaid ? "text-green-600 border-green-600" : ""
                }>
                  {isFullyPaid ? i18n.__('invoicePayment.paid') : i18n.__('invoicePayment.due')}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">{i18n.__('invoicePayment.totalAmount')}</Label>
                  <CurrencyDisplay amount={invoice.amount} 
                    currency={invoice.currency}
                    className="text-lg font-semibold" currencyService={currencyService}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">{i18n.__('invoicePayment.amountPaid')}</Label>
                  <CurrencyDisplay amount={Math.max(invoice.paidAmount, paidFromHistory)} 
                    currency={invoice.currency}
                    className="text-lg font-semibold text-green-600" currencyService={currencyService}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">{i18n.__('invoicePayment.amountDue')}</Label>
                  <CurrencyDisplay amount={displayRemaining} 
                    currency={invoice.currency}
                    className={`text-lg font-semibold ${
                      isFullyPaid ? 'text-green-600' : 'text-red-600'
                    }`} currencyService={currencyService}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">{i18n.__('invoicePayment.paymentsMade')}</Label>
                  <div className="text-lg font-semibold">{invoice.paymentCount}</div>
                </div>
              </div>

              {invoice.message && (
                <div>
                  <Label className="text-sm font-medium text-gray-500">{i18n.__('invoicePayment.description')}</Label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{invoice.message}</p>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium text-gray-500">{i18n.__('invoicePayment.expires')}</Label>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>{formatDistanceToNow(new Date(invoice.expiryDate), { addSuffix: true })}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment History */}
          {invoice.payments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{i18n.__('invoicePayment.history')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {invoice.payments.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <div>
                          <div className="font-medium">{i18n.__('invoicePayment.received')}</div>
                          <div className="text-sm text-gray-500">
                            {formatDistanceToNow(new Date(payment.date), { addSuffix: true })}
                            {payment.payerName && ` • ${i18n.__('invoicePayment.from', { name: payment.payerName })}`}
                          </div>
                        </div>
                      </div>
                      <CurrencyDisplay amount={payment.amount} 
                        currency={payment.currency}
                        className="font-semibold text-green-600" currencyService={currencyService}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Payment Panel */}
        <div className="space-y-6">
          {!isFullyPaid ? (
            <InvoicePaymentDecision
              isGuest={isGuest}
              invoiceData={{
                invoiceKey: invoice.key,
                invoiceAmount: invoice.amount,
                invoiceCurrency: invoice.currency,
                invoiceMessage: invoice.message,
                invoiceCreator: invoice.creatorName,
                remainingAmount: displayRemaining,
              }}
              onPaymentComplete={handlePaymentComplete}
              onPaymentError={handlePaymentError}
              onCancel={handleCancel}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>{i18n.__('invoicePayment.paidTitle')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Alert className="border-green-200 bg-green-50 text-green-800">
                  <AlertDescription>
                    {i18n.__('invoicePayment.paidDesc')}
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicePaymentPage;
